import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, ElementRef, Renderer2, RendererFactory2, HostListener } from '@angular/core';

@Component({
	selector: 'app-multi-select-checkbox',
	templateUrl: './multi-select-checkbox.component.html',
	styleUrls: ['./multi-select-checkbox.component.scss'],
})
export class MultiSelectCheckboxComponent implements OnInit, OnChanges, OnDestroy {
	searchTerm: string;
	showMenu: boolean;
	autoCompleteOptionIndex: number;
	clickedInside: boolean;
	selectedValuesText: string;
	localOptions: any[];
	filteredLocalOptions: any[];
	localSelectedOptions: any[];
	changedOptionsDict: {};
	private renderer: Renderer2;

	// default list of options
	@Input()
	options: any[];

	// list of preselected options (should be a subset of the 'options' input)
	@Input()
	selectedOptions: any[];

	// property name to be displayed in the dropdown
	@Input()
	key: string;

	// label to display above the multi-select
	@Input()
	multiSelectLabel: string;

	// string to use when no options are selected
	@Input()
	unselectedLabel: string;

	// string to represent the plural form of the option
	// i.e. a multi-select of cat objects might have a plural name of "Cats"
	@Input()
	pluralLabel: string;

	// If true, a dark color scheme will be applied
	@Input()
	darkMode: boolean;

	// indicates if the options presented are grouped
	// If true, the top level object will be the group and a given property will be expected
	// to contain an array of data
	@Input()
	groupedItems: boolean;

	// property name to be displayed for the group object
	// only needed if displaying grouped items
	@Input()
	groupKey: string;

	// the property name of the property within a group that contains the desired array of data
	// only needed if displaying grouped items
	@Input()
	groupedOptions: string;

	@Input()
	allSelected: boolean;

	// the name of the key which contains the subtext to display
	// if left as an empty string, no subtext will be displayed
	// if a string is passed, the it will be used as they dict key to try to display text
	@Input()
	subTextKey: string = '';

	@Input()
	showChange: boolean = false;

	// sends the selected options back to the parent
	@Output()
	optionsChanged = new EventEmitter<any[]>();

	constructor(private el: ElementRef, private rendererFactory: RendererFactory2) {
		this.renderer = rendererFactory.createRenderer(null, null);
		this.searchTerm = '';
		this.filteredLocalOptions = [];
		this.changedOptionsDict = {};
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.setMaxHeight();
	}

	ngOnInit() {
		this.setMaxHeight();
	}

	ngOnChanges() {
		if (this.options) {
			this.localOptions = JSON.parse(JSON.stringify(this.options));
			const propertyName = this.groupedItems ? this.groupKey : this.key;
			this.localOptions.sort((a, b) => {
				if (a[propertyName] < b[propertyName]) {
					return -1;
				}
				if (a[propertyName] > b[propertyName]) {
					return 1;
				}
				return 0;
			});
			this.search('');
		}
		if (this.selectedOptions) {
			this.localSelectedOptions = JSON.parse(JSON.stringify(this.selectedOptions));
		} else {
			this.localSelectedOptions = [];
		}
		this.updateText();
		if (this.localOptions && this.localSelectedOptions) {
			this.localOptions = this.localOptions.map(option => {
				const found = this.localSelectedOptions.find(selectedOption => selectedOption.id === option.id);
				const changed = option.id in this.changedOptionsDict ? true : false;

				if (found) {
					return { ...option, msSelected: true, changed: changed };
				} else {
					return { ...option, msSelected: false, changed: changed };
				}
			});
		}
		this.filteredLocalOptions = this.localOptions;
		this.checkIfAllSelected();
	}

	selectAll() {
		this.allSelected = !this.allSelected;
		this.localSelectedOptions = [];
		this.localOptions.forEach(option => {
			if (option.msSelected !== this.allSelected) {
				this.handleChange(option);
			}

			option['msSelected'] = this.allSelected;

			if (this.groupedItems) {
				option[this.groupedOptions].forEach(go => {
					go['msSelected'] = this.allSelected;
					if (this.allSelected) {
						this.localSelectedOptions.push(go);
					}
				});
			} else {
				if (this.allSelected) {
					this.localSelectedOptions.push(option);
				}
			}
		});
		this.filteredLocalOptions = this.localOptions;
		this.updateText();
		this.optionsChanged.emit(this.localSelectedOptions);
	}

	private setMaxHeight() {
		const element = this.el.nativeElement.parentNode;
		let totalTop = 0;
		totalTop = this.el.nativeElement.getBoundingClientRect().top;
		totalTop += element.getBoundingClientRect().top;

		const maxHeight = document.body.scrollHeight - (totalTop + window.pageYOffset) - 200;
		this.renderer.setStyle(this.el.nativeElement.getElementsByClassName('multi-select__menu-scrollbar')[0], 'max-height', maxHeight + 'px');
	}

	selectOption(option: any) {
		option.msSelected = !option.msSelected;
		if (option.msSelected) {
			this.handleChange(option);
			this.localSelectedOptions.push(option);
		} else {
			this.handleChange(option);
			const index = this.localSelectedOptions.findIndex(opt => opt.id === option.id);
			this.localSelectedOptions.splice(index, 1);
		}
		this.checkIfAllSelected();
		this.optionsChanged.emit(this.localSelectedOptions);
	}

	handleChange(option) {
		if (!this.showChange) {
			return;
		}

		if (option.id in this.changedOptionsDict) {
			delete this.changedOptionsDict[option.id];
		} else {
			this.changedOptionsDict[option.id] = option;
		}
	}

	selectGroup(group: any) {
		group.msSelected = !group.msSelected;

		group[this.groupedOptions].forEach(option => {
			option.msSelected = false;
			const index = this.localSelectedOptions.findIndex(opt => opt.id === option.id);
			if (index >= 0) {
				this.localSelectedOptions.splice(index, 1);
			}
			if (group.msSelected) {
				this.selectOption(option);
			}
		});

		this.checkIfAllSelected();
	}

	checkIfAllSelected() {
		if (this.localOptions) {
			if (!this.groupedItems) {
				this.allSelected = this.localOptions.every(opt => opt['msSelected'] === true);
			} else {
				this.allSelected = this.localOptions.every(group => {
					return group[this.groupedOptions].every(option => option['msSelected'] === true);
				});
			}
		}
		this.updateText();
	}

	toggleMenu() {
		this.showMenu = !this.showMenu;
	}

	closeMenu() {
		this.showMenu = false;
	}

	setSearchTerm(newSearchTerm: string) {
		this.searchTerm = newSearchTerm;
	}

	updateText() {
		if (this.allSelected) {
			this.selectedValuesText = 'All';
		} else if (this.localSelectedOptions && this.localSelectedOptions.length === 0) {
			this.selectedValuesText = this.unselectedLabel;
		} else if (this.localSelectedOptions.length === 1) {
			this.selectedValuesText = this.localSelectedOptions[0][this.key];
		} else if (this.localSelectedOptions.length >= 2) {
			this.selectedValuesText = `${this.localSelectedOptions.length} ${this.pluralLabel} selected`;
		}
	}

	clickOutside() {
		if (this.clickedInside === false) {
			this.closeMenu();
		}
		this.clickedInside = false;
	}

	search(searchString: string) {
		const key = this.key;
		this.filteredLocalOptions = [];
		this.searchTerm = searchString;
		this.localOptions.forEach(option => {
			if (!this.groupedItems) {
				if (option[key].toLowerCase().includes(searchString.toLowerCase())) {
					if (!this.filteredLocalOptions.some(fo => fo.id === option.id)) {
						this.filteredLocalOptions.push(option);
					}
				}
			} else {
				const groupKey = this.groupKey;
				const groupedOptionsKey = this.groupedOptions;
				if (option[groupKey].toLowerCase().includes(searchString.toLowerCase())) {
					if (!this.filteredLocalOptions.some(fo => fo.id === option.id)) {
						this.filteredLocalOptions.push(option);
					}
				} else {
					option[groupedOptionsKey].forEach(groupedOption => {
						if (groupedOption[key].toLowerCase().includes(searchString.toLowerCase())) {
							if (!this.filteredLocalOptions.some(fo => fo.id === option.id)) {
								this.filteredLocalOptions.push(option);
							}
						}
					});
				}
			}
		});
	}

	ngOnDestroy() {
		this.selectedValuesText = '';
	}
}
